/**
 * src/app/venue/VenueProtectedLayout.tsx — 2004va
 *
 * Layout for authenticated venue-mode pages. Structure:
 *
 *   ┌─────────────────────────────┐
 *   │  Header: venue name + mode  │
 *   │  ─────────────────────────  │
 *   │                             │
 *   │        <Routes />           │
 *   │                             │
 *   │  ─────────────────────────  │
 *   │  Bottom nav: 4 tabs         │
 *   └─────────────────────────────┘
 *
 * Guards against missing picker session by navigating to /venue/enter.
 * Loads digest stats so the bottom nav shows live counts.
 *
 * Context-difference from creator side: the header accent is gold not
 * magenta. Same tokens, same layout, one visual signal of "you're in the
 * venue workspace."
 */

import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom'
import { getStoredRole, ROLE_CONFIGS } from '@/lib/venue-role'
import { useQuery } from '@tanstack/react-query'
import { Spinner } from '@/components/ui'
import {
  fetchVenueBySlug,
  fetchDigestStats,
  getPickerSession,
  touchPickerSession,
  createDemoSession,
} from '@/lib/venue'
import { FeedbackButton } from '@/components/ui/FeedbackButton'
import { VenueBottomNav } from './VenueBottomNav'
import { MorningDigestPage }  from '@/pages/venue/MorningDigestPage'
import { ShortlistPage }       from '@/pages/venue/ShortlistPage'
import { LibraryPage }         from '@/pages/venue/LibraryPage'
import { ApprovalQueuePage }  from '@/pages/venue/ApprovalQueuePage'
import { PublishQueuePage }   from '@/pages/venue/PublishQueuePage'
import { VenueSearchPage }    from '@/pages/venue/VenueSearchPage'
import { InflowPage } from '@/pages/venue/InflowPage'
import { PickerPage }         from '@/pages/venue/PickerPage'

function RoleRedirect({ slug }: { slug: string }) {
  const role = getStoredRole(slug)
  if (!role) return <Navigate to={`/venue/${slug}/enter`} replace />
  const config = ROLE_CONFIGS[role as keyof typeof ROLE_CONFIGS]
  if (!config) return <Navigate to={`/venue/${slug}/enter`} replace />
  return <Navigate to={`/venue/${slug}/${config.defaultTab}`} replace />
}

export function VenueProtectedLayout() {
  const { slug } = useParams<{ slug: string }>()
  const navigate  = useNavigate()
  const location  = useLocation()
  const isPicker  = location.pathname.endsWith('/picker')

  // Must have a venue slug
  if (!slug) return <Navigate to="/venue" replace />

  // Must have an active picker session for this venue — auto-create if missing
  const [session, setSession] = useState<ReturnType<typeof getPickerSession>>(getPickerSession(slug))
  const [autoCreating, setAutoCreating] = useState(false)

  useEffect(() => {
    if (session || autoCreating) return
    setAutoCreating(true)
    createDemoSession(slug).then(s => {
      if (s) setSession(s)
      setAutoCreating(false)
    })
  }, [slug, session, autoCreating])

  // Venue metadata
  const { data: venue, isLoading: venueLoading, error: venueError } = useQuery({
    queryKey: ['venue', slug],
    queryFn:  () => fetchVenueBySlug(slug),
    staleTime: 5 * 60_000,
  })

  // Live stats for the bottom-nav badges
  const { data: stats } = useQuery({
    queryKey: ['venue-digest-stats', venue?.id],
    queryFn:  () => fetchDigestStats({ venue_id: venue!.id }),
    enabled:  !!venue?.id,
    staleTime: 5 * 60_000,
  })

  // Heartbeat: keep the picker session alive
  useEffect(() => {
    if (!session?.picker_id) return
    const id = setInterval(() => {
      touchPickerSession(session.picker_id).catch(() => {})
    }, 5 * 60_000)
    return () => clearInterval(id)
  }, [session?.picker_id])

  // Bad slug → back to entry
  useEffect(() => {
    if (!venueLoading && !venue && !venueError) {
      navigate('/venue', { replace: true })
    }
  }, [venueLoading, venue, venueError, navigate])

  if (venueLoading || autoCreating || !session) {
    return (
      <div className="min-h-screen bg-room-bg flex items-center justify-center">
        <Spinner size={32} />
      </div>
    )
  }

  if (venueError || !venue) {
    return (
      <div className="min-h-screen bg-room-bg flex items-center justify-center px-6">
        <div className="text-center space-y-3">
          <p className="text-4xl">⚠️</p>
          <p className="text-white font-semibold">Venue not found</p>
          <p className="text-room-muted text-sm">
            The venue &ldquo;{slug}&rdquo; could not be loaded.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-room-bg text-white relative">

      {/* Ambient glow — subtler than the creator side, gold not magenta */}
      {!isPicker && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-room-gold/5 rounded-full blur-[140px]" />
        </div>
      )}

      <div className={isPicker ? '' : 'relative z-10 max-w-lg mx-auto px-4 pt-6 pb-28'}>

        {/* Venue header — hidden on picker (picker is full-screen with its own chrome) */}
        {!isPicker && (
          <header className="mb-6 space-y-1.5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-room-gold font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-room-gold animate-pulse-dot" />
              Picker mode · {venue.name}
            </div>
            <h1 className="text-3xl font-black tracking-tight text-white leading-tight">
              {venue.name}
            </h1>
            {session.display_name && (
              <p className="text-xs text-room-muted">
                Signed in as {session.display_name}
              </p>
            )}
          </header>
        )}

        <Routes>
          <Route path="digest"    element={<MorningDigestPage venue={venue} picker={session} />} />
          <Route path="approvals" element={<ApprovalQueuePage venue={venue} picker={session} />} />
          <Route path="queue"     element={<PublishQueuePage  venue={venue} picker={session} />} />
          <Route path="search"    element={<VenueSearchPage   venue={venue} picker={session} />} />
          <Route path="inflow"    element={<InflowPage    venue={venue} picker={session} />} />
          <Route path="shortlist"  element={<ShortlistPage       venue={venue} picker={session} />} />
          <Route path="library"    element={<LibraryPage          venue={venue} picker={session} />} />
          <Route path="picker"    element={<PickerPage        venue={venue} picker={session} onBack={() => navigate(`/venue/${slug}/shortlist`, { replace: true })} />} />
          <Route path="*"         element={<RoleRedirect slug={slug ?? ''} />} />
        </Routes>
      </div>

      <FeedbackButton venueSlug={slug ?? ''} currentPage={location.pathname} />
      {!isPicker && (
        <VenueBottomNav
          venueSlug={slug}
          venueId={venue?.id}
          unactionedCount={stats?.unactioned ?? 0}
          pendingApprovals={stats?.needs_approval ?? 0}
          queuedCount={stats?.posted ?? 0}
        />
      )}
    </div>
  )
}
